/**
 * NotesSection
 *
 * Notes & Images section inside the TradeDrawer — renders as the Docs tab content.
 *
 * Four phase groups (Pre-Entry | Entry | Management | Exit), each with:
 *   - Category buttons from LiveTradingSettings.noteCategories (showOnTradeEntry = true)
 *   - [📷 Image] button — toggles an inline image picker
 *     • URL input (external TradingView / broker screenshot link)
 *     • ImageFieldInput dropzone (local PNG/JPEG/WebP upload via ImageStorageService)
 *     • Mutual exclusion: URL filled → upload disabled; image uploaded → URL disabled
 *   - Inline text input when a category button is clicked
 *
 * "Added Notes & Images" area below all phase groups:
 *   - Notes loaded from the appropriate service (see ownership model below)
 *   - In-memory images (TradeImageRef[]) tracked by phase
 *   - Image cards show a live thumbnail; clicking opens a lightbox preview
 *   - Each item individually deletable
 *
 * Ownership model (T12 correctness fix #3):
 *   - Draft mode (tradeId not set):      reads/writes NoteDraft (ownerId=draftId)
 *   - Persistent-trade mode (tradeId set): reads/writes Note (ownerId=tradeId)
 *
 * @see noteDraftService — draft-mode note persistence
 * @see noteService — persistent-trade note persistence
 * @see ImageFieldInput — file upload + ImageStorageService integration
 * @see ImageRefThumbnail — inline sub-component for thumbnail + lightbox
 * @see NoteTradePhase — 'order' | 'entry' | 'management' | 'exit' | 'review'
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Image, X } from 'lucide-react';

// 2. Internal absolute imports
import { Button, Input, Divider } from '@enfomentis/ui';
import { noteDraftService } from '@/services/NoteDraftService';
import { noteService } from '@/services/NoteService';
import { tradeService } from '@/services/TradeService';
import { imageStorageService } from '@/services/ImageStorageService';
import { ImageFieldInput } from '@/components/TradingHub/PreTradingWorkflow/ImageFieldInput';
import { ImageLightbox } from '@/components/shared/ImageHandling';

// 3. Type imports
import type { Note, NoteTradePhase } from '@/types/schema/Note';
import type { NoteDraft } from '@/types/transient/NoteDraft';
import type { LiveTradingSettings } from '@/types/schema/LiveTradingSettings';
import type { ImageReference } from '@/types/schema/StoredImage';
import type { TradeImageRef } from '@/types/shared/TradeImageRef';

// ===== TYPES =====

type NoteCategory = LiveTradingSettings['noteCategories'][number];

interface NotesSectionProps {
  /** In-memory trade draft ID — used as ownerId for NoteDraftService in draft mode */
  draftId: string;
  /** Note categories from LiveTradingSettings (user-configured) */
  noteCategories: NoteCategory[];
  /**
   * Called whenever the in-memory images state changes.
   * Allows TradeDrawer to lift images for inclusion in TradeDraft on commit.
   * Fires on both seed (drawer open) and user actions — do NOT use this to
   * mark dirty; use onImagesDirty instead.
   */
  onImagesChange?: (images: Partial<Record<NotePhase, TradeImageRef[]>>) => void;
  /**
   * Called only when the user explicitly adds or deletes an image.
   * Fires after onImagesChange so TradeDrawer can mark isDirty=true without
   * reacting to the initial seed on Docs tab mount.
   */
  onImagesDirty?: () => void;
  /**
   * Initial images to seed state when the drawer opens (edit mode).
   * Read via ref at draftId-change time — does not trigger re-seed on re-renders.
   */
  initialImages?: Partial<Record<NotePhase, TradeImageRef[]>>;
  /**
   * T12: Promoted trade ID — when set, notes write to Note(ownerId=tradeId).
   * When absent (draft mode), notes write to NoteDraft(ownerId=draftId).
   */
  tradeId?: string;
}

type NotePhase = 'order' | 'entry' | 'management' | 'exit';

const NOTE_PHASES: { key: NotePhase; label: string; tradePhase: NoteTradePhase }[] = [
  { key: 'order', label: 'Pre-Entry', tradePhase: 'order' },
  { key: 'entry', label: 'Entry', tradePhase: 'entry' },
  { key: 'management', label: 'Management', tradePhase: 'management' },
  { key: 'exit', label: 'Exit', tradePhase: 'exit' },
];

/** Display label for a tradePhase value — maps 'order' → 'Pre-Entry', capitalises others. */
const phaseDisplayLabel = (phase: NoteTradePhase | undefined): string => {
  if (!phase) return '';
  if (phase === 'order') return 'Pre-Entry';
  return phase.charAt(0).toUpperCase() + phase.slice(1);
};

interface ActiveNoteInput {
  phase: NotePhase;
  tradePhase: NoteTradePhase;
  categoryName: string;
  categoryEmoji: string;
}

// ===== IMAGE THUMBNAIL SUB-COMPONENT =====

/**
 * ImageRefThumbnail
 *
 * Renders a 40×40 thumbnail for a TradeImageRef and opens a fullscreen
 * lightbox on click. Self-contained: resolves imageId → objectURL via
 * imageStorageService, or uses the external url directly.
 *
 * Falls back to a placeholder icon if the URL can't be loaded.
 */
interface ImageRefThumbnailProps {
  imageId: string | undefined;
  url: string | undefined;
  label: string;
}

const ImageRefThumbnail: React.FC<ImageRefThumbnailProps> = ({ imageId, url, label }) => {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(url ?? null);
  const [hasError, setHasError] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // For imageId refs: async-resolve the objectURL from ImageStorageService
  useEffect(() => {
    if (!imageId) return;
    let cancelled = false;
    imageStorageService.getImageUrl(imageId)
      .then((u) => { if (!cancelled) setResolvedUrl(u); })
      .catch(() => { if (!cancelled) setHasError(true); });
    return () => { cancelled = true; };
  }, [imageId]);

  const canPreview = resolvedUrl !== null && !hasError;

  return (
    <>
      {/* Thumbnail — clickable when URL is available */}
      <div
        className={[
          'w-10 h-10 rounded-small shrink-0 overflow-hidden bg-default-100 flex items-center justify-center',
          canPreview ? 'cursor-pointer' : '',
        ].join(' ')}
        onClick={() => { if (canPreview) setIsLightboxOpen(true); }}
        role={canPreview ? 'button' : undefined}
        aria-label={canPreview ? `Preview ${label}` : undefined}
        tabIndex={canPreview ? 0 : undefined}
        onKeyDown={(e) => {
          if (canPreview && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setIsLightboxOpen(true);
          }
        }}
      >
        {resolvedUrl !== null && !hasError ? (
          <img
            src={resolvedUrl}
            alt={label}
            className="w-full h-full object-cover"
            onError={() => setHasError(true)}
          />
        ) : (
          <Image size={14} className="text-text-tertiary" />
        )}
      </div>

      {/* Lightbox — only mounted when there's a previewable URL */}
      {canPreview && (
        <ImageLightbox
          isOpen={isLightboxOpen}
          onClose={() => setIsLightboxOpen(false)}
          imageUrl={resolvedUrl}
          alt={label}
        />
      )}
    </>
  );
};

// ===== MAIN COMPONENT =====

export const NotesSection: React.FC<NotesSectionProps> = ({
  draftId,
  noteCategories,
  onImagesChange,
  onImagesDirty,
  initialImages,
  tradeId,
}) => {
  // ---------------------------------------------------------------------------
  // Note state — NoteDraft[] in draft mode, Note[] in persistent-trade mode
  // ---------------------------------------------------------------------------

  const [notes, setNotes] = useState<Array<NoteDraft | Note>>([]);
  const [activeInput, setActiveInput] = useState<ActiveNoteInput | null>(null);
  const [noteText, setNoteText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // ---------------------------------------------------------------------------
  // Image picker state
  // ---------------------------------------------------------------------------

  // initialImages ref — synced at render time so the draftId-change effect
  // always seeds with the latest value without adding initialImages to its deps.
  const initialImagesRef = useRef(initialImages);
  initialImagesRef.current = initialImages;

  const [images, setImages] = useState<Partial<Record<NotePhase, TradeImageRef[]>>>({});
  // Synced ref so handleAddImage / handleDeleteImage always read the current images
  // without adding `images` to their useCallback deps (avoids needless recreation).
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const [activeImageInput, setActiveImageInput] = useState<NotePhase | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imageRef, setImageRef] = useState<ImageReference | null>(null);

  // ---------------------------------------------------------------------------
  // T12: Load notes from the correct service based on mode
  // ---------------------------------------------------------------------------

  const loadNotes = useCallback(async (): Promise<void> => {
    try {
      if (tradeId) {
        // Persistent-trade mode — read from Note table
        const loaded = await noteService.getByOwner('trade', tradeId);
        setNotes(loaded);
      } else {
        // Draft mode — read from NoteDraft table
        if (!draftId) return;
        const loaded = await noteDraftService.getByOwner('trade', draftId);
        setNotes(loaded);
      }
    } catch {
      // silent — empty state shown
    }
  }, [draftId, tradeId]);

  useEffect(() => {
    void loadNotes();
  }, [loadNotes]);

  // Reset image state when draftId changes (drawer opened fresh or closed) or when
  // tradeId first becomes available (promotedTrade loads async — seeds from Trade.images).
  useEffect(() => {
    setImages(initialImagesRef.current ?? {});
    setActiveImageInput(null);
    setImageUrl('');
    setImageRef(null);
  }, [draftId, tradeId]);

  // Sync images state up to TradeDrawer whenever it changes
  useEffect(() => {
    onImagesChange?.(images);
  }, [images, onImagesChange]);

  // ---------------------------------------------------------------------------
  // Note creation
  // ---------------------------------------------------------------------------

  const handleCategoryClick = useCallback((
    phase: NotePhase,
    tradePhase: NoteTradePhase,
    category: NoteCategory,
  ): void => {
    if (activeInput?.phase === phase && activeInput.categoryName === category.name) {
      setActiveInput(null);
      setNoteText('');
      return;
    }
    setActiveInput({ phase, tradePhase, categoryName: category.name, categoryEmoji: category.emoji });
    setNoteText('');
  }, [activeInput]);

  const handleSaveNote = useCallback(async (): Promise<void> => {
    if (!activeInput || !noteText.trim()) return;
    if (!tradeId && !draftId) return;
    setIsSaving(true);
    try {
      if (tradeId) {
        // T12: Persistent-trade mode — write to Note table (ownerId = tradeId)
        await noteService.add({
          noteType: 'trade',
          captureContext: 'live-trading',
          ownerId: tradeId,
          tradePhase: activeInput.tradePhase,
          category: activeInput.categoryName,
          text: noteText.trim(),
          timestamp: new Date().toISOString(),
        });
      } else {
        // Draft mode — write to NoteDraft table (ownerId = draftId)
        await noteDraftService.add({
          noteType: 'trade',
          captureContext: 'live-trading',
          ownerId: draftId,
          tradePhase: activeInput.tradePhase,
          category: activeInput.categoryName,
          text: noteText.trim(),
          timestamp: new Date().toISOString(),
        });
      }
      setActiveInput(null);
      setNoteText('');
      await loadNotes();
    } catch {
      // silent
    } finally {
      setIsSaving(false);
    }
  }, [activeInput, noteText, draftId, tradeId, loadNotes]);

  const handleCancelInput = useCallback((): void => {
    setActiveInput(null);
    setNoteText('');
  }, []);

  // ---------------------------------------------------------------------------
  // Note deletion
  // ---------------------------------------------------------------------------

  const handleDeleteNote = useCallback(async (noteId: string): Promise<void> => {
    try {
      if (tradeId) {
        // T12: Persistent-trade mode — delete from Note table
        await noteService.softDelete(noteId);
      } else {
        await noteDraftService.softDelete(noteId);
      }
      await loadNotes();
    } catch {
      // silent
    }
  }, [tradeId, loadNotes]);

  // ---------------------------------------------------------------------------
  // Image picker handlers
  // ---------------------------------------------------------------------------

  const handleImageButtonClick = useCallback((phase: NotePhase): void => {
    if (activeImageInput === phase) {
      setActiveImageInput(null);
      setImageUrl('');
      setImageRef(null);
      return;
    }
    setActiveImageInput(phase);
    setImageUrl('');
    setImageRef(null);
  }, [activeImageInput]);

  const handleAddImage = useCallback((phase: NotePhase): void => {
    const trimmedUrl = imageUrl.trim();
    if (!trimmedUrl && imageRef === null) return;
    const ref: TradeImageRef = imageRef !== null
      ? { imageId: imageRef.imageId, timestamp: new Date().toISOString() }
      : { url: trimmedUrl, timestamp: new Date().toISOString() };
    const updated = { ...imagesRef.current, [phase]: [...(imagesRef.current[phase] ?? []), ref] };
    setImages(updated);
    // Fix #7: persist to Trade in persistent-trade mode so images survive drawer close/reopen
    if (tradeId) {
      void tradeService.updateImages(tradeId, updated);
    }
    // Fix #8: signal user action so TradeDrawer can mark isDirty (separate from onImagesChange
    // which also fires on seed — we only want dirty on explicit user add/delete)
    onImagesDirty?.();
    setActiveImageInput(null);
    setImageUrl('');
    setImageRef(null);
  }, [imageUrl, imageRef, tradeId, onImagesDirty]);

  const handleDeleteImage = useCallback((phase: NotePhase, timestamp: string): void => {
    const updated = {
      ...imagesRef.current,
      [phase]: (imagesRef.current[phase] ?? []).filter((img) => img.timestamp !== timestamp),
    };
    setImages(updated);
    // Fix #7: persist to Trade in persistent-trade mode so deletion survives drawer close/reopen
    if (tradeId) {
      void tradeService.updateImages(tradeId, updated);
    }
    // Fix #8: signal user action (see handleAddImage comment)
    onImagesDirty?.();
  }, [tradeId, onImagesDirty]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const visibleCategories = noteCategories.filter((c) => c.showOnTradeEntry);

  const allImages = NOTE_PHASES.flatMap((p) =>
    (images[p.key] ?? []).map((img) => ({ ...img, phaseKey: p.key, phaseLabel: p.label }))
  );

  const hasAddedItems = notes.length > 0 || allImages.length > 0;

  // ---------------------------------------------------------------------------
  // Render a single phase group
  // ---------------------------------------------------------------------------

  const renderPhaseGroup = (phase: { key: NotePhase; label: string; tradePhase: NoteTradePhase }): React.ReactNode => {
    const isNoteInputActiveHere = activeInput?.phase === phase.key;
    const isImagePickerOpen = activeImageInput === phase.key;
    const urlFilled = imageUrl.trim().length > 0;
    const imageFilled = imageRef !== null;

    return (
      <div key={phase.key}>
        {/* Phase label */}
        <p className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-1.5">
          {phase.label}
        </p>

        {/* Category buttons + image button */}
        {/* Outer row: categories wrap freely in flex-1; image button is a shrink-0 sibling so it never wraps */}
        <div className="flex items-start gap-1">
          <div className="flex flex-wrap gap-1 flex-1">
            {visibleCategories.length > 0 ? (
              visibleCategories.map((cat) => {
                const isActive = isNoteInputActiveHere && activeInput?.categoryName === cat.name;
                return (
                  <Button
                    key={cat.name}
                    size="sm"
                    variant={isActive ? 'solid' : 'flat'}
                    color={isActive ? 'primary' : 'default'}
                    className="h-6 px-2 text-xs"
                    onPress={() => handleCategoryClick(phase.key, phase.tradePhase, cat)}
                  >
                    {cat.emoji} {cat.name}
                  </Button>
                );
              })
            ) : (
              <span className="text-xs text-text-tertiary italic">No categories configured</span>
            )}
          </div>

          {/* Image button — always top-right, never participates in category wrap */}
          <Button
            size="sm"
            variant={isImagePickerOpen ? 'solid' : 'bordered'}
            color={isImagePickerOpen ? 'primary' : 'default'}
            className="h-6 px-2 text-xs shrink-0"
            startContent={<Image size={10} />}
            aria-label={`Add image for ${phase.label} phase`}
            onPress={() => handleImageButtonClick(phase.key)}
          >
            Image
          </Button>
        </div>

        {/* Inline note text input */}
        {isNoteInputActiveHere && activeInput !== null && (
          <div className="mt-2 flex flex-col gap-1.5">
            <textarea
              className="w-full text-xs rounded-medium border border-default-300 bg-content1 px-2 py-1.5 text-text-primary placeholder:text-text-tertiary resize-none focus:outline-none focus:border-primary"
              rows={2}
              placeholder={`${activeInput.categoryEmoji} ${activeInput.categoryName} note…`}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  void handleSaveNote();
                }
              }}
              autoFocus
            />
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                color="primary"
                variant="solid"
                className="h-6 px-2 text-xs"
                isLoading={isSaving}
                isDisabled={!noteText.trim() || isSaving}
                onPress={() => void handleSaveNote()}
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="light"
                className="h-6 px-2 text-xs"
                onPress={handleCancelInput}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Inline image picker */}
        {isImagePickerOpen && (
          <div className="mt-2 flex flex-col gap-2">
            {/* URL input — disabled when a file has been uploaded */}
            <Input
              label="Image URL"
              placeholder="https://… (TradingView, broker screenshot)"
              size="sm"
              variant="bordered"
              value={imageUrl}
              onValueChange={setImageUrl}
              isDisabled={imageFilled}
            />

            <div className="flex items-center gap-2 py-0.5">
              <Divider className="flex-1" />
              <span className="text-xs text-text-tertiary px-1">or</span>
              <Divider className="flex-1" />
            </div>

            {/* File upload — disabled when a URL has been entered */}
            <ImageFieldInput
              fieldId={`trade-image-${phase.key}`}
              value={imageRef}
              onChange={setImageRef}
              label="Upload image"
              description="PNG, JPEG, or WebP — max 5 MB"
              disabled={urlFilled}
            />

            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                color="primary"
                variant="solid"
                className="h-6 px-2 text-xs"
                isDisabled={!urlFilled && !imageFilled}
                onPress={() => handleAddImage(phase.key)}
              >
                Add Image
              </Button>
              <Button
                size="sm"
                variant="light"
                className="h-6 px-2 text-xs"
                onPress={() => {
                  setActiveImageInput(null);
                  setImageUrl('');
                  setImageRef(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col gap-3">
      {/* Phase groups */}
      {NOTE_PHASES.map(renderPhaseGroup)}

      {/* Added Notes & Images */}
      {hasAddedItems && (
        <div className="flex flex-col gap-1.5 mt-1">
          <p className="text-xs font-medium text-text-tertiary uppercase tracking-wide">
            Added Notes &amp; Images
          </p>

          {/* Notes */}
          {notes.map((note) => (
            <div
              key={note.id}
              className="flex items-start gap-1.5 rounded-medium border border-default-200 bg-default-50 px-2 py-1.5"
            >
              <div className="flex-1 min-w-0">
                <p className="text-xs text-text-tertiary">
                  {phaseDisplayLabel(note.tradePhase)}{note.category ? ` · ${note.category}` : ''}
                </p>
                <p className="text-xs text-text-primary mt-0.5 break-words">{note.text}</p>
              </div>
              <Button
                size="sm"
                isIconOnly
                variant="light"
                className="shrink-0 h-5 w-5 min-w-0"
                aria-label="Delete note"
                onPress={() => void handleDeleteNote(note.id)}
              >
                <X size={10} />
              </Button>
            </div>
          ))}

          {/* Images — thumbnail card with click-to-preview */}
          {allImages.map((img) => (
            <div
              key={img.timestamp}
              className="flex items-center gap-2 rounded-medium border border-default-200 bg-default-50 p-1.5"
            >
              {/* Thumbnail resolves imageId → objectURL or uses url directly */}
              <ImageRefThumbnail
                imageId={img.imageId}
                url={img.url}
                label={`${img.phaseLabel} image`}
              />

              <div className="flex-1 min-w-0">
                <p className="text-xs text-text-tertiary">{img.phaseLabel} · Image</p>
                <p className="text-xs text-text-primary mt-0.5 truncate">
                  {img.imageId !== undefined ? 'Uploaded' : (img.url ?? '')}
                </p>
              </div>

              <Button
                size="sm"
                isIconOnly
                variant="light"
                className="shrink-0 h-5 w-5 min-w-0"
                aria-label="Delete image"
                onPress={() => handleDeleteImage(img.phaseKey, img.timestamp)}
              >
                <X size={10} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
